/* Product-aware predictive search: one native HTML section request per query.
 * JSON is a failure-only fallback and never pairs aggregate comparison prices. */
(function(root,factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) root.OptionalCaseSearch = api;
})(typeof window !== 'undefined' ? window : null,function(win) {
  'use strict';
  const states = new WeakMap();
  const sectionCooldowns = new Map();
  const debounceMilliseconds = 250;
  const cooldownMilliseconds = 15000;
  const allowedTypes = new Set(['product','page','article']);
  function types(value) {
    const selected = String(value || 'product').split(',');
    return selected.length && selected.every(type => allowedTypes.has(type)) ? [...new Set(selected)].join(',') : 'product';
  }
  function route(value, ending) {
    try {
      const url = new URL(value,win.location.origin);
      if (url.origin !== win.location.origin || url.search || url.hash || !new RegExp('^/(?:[a-zA-Z0-9-]+/)*' + ending + '$').test(url.pathname)) return null;
      return url;
    } catch (_) { return null; }
  }
  function configuration() {
    try {
      const tags = win.document.querySelectorAll('[data-optional-case-search-config]');
      if (tags.length !== 1) return null;
      const config = JSON.parse(tags[0].textContent);
      const suggest = route(config.suggestUrl,'search/suggest'), search = route(config.searchUrl,'search');
      if (config.sectionId !== 'optional-case-predictive-search' || !suggest || !search || !/^[A-Z]{3}$/.test(config.currency)) return null;
      return {...config,suggest,search};
    } catch (_) { return null; }
  }
  function searchURL(config, form, term, type) {
    const url = config?.search || route(form.action,'search') || new URL('/search',win.location.origin);
    const result = new URL(url.href);
    result.searchParams.set('type',types(type));result.searchParams.set('q',term + '*');
    return result.href;
  }
  function resourceURL(value, config, removeVariant) {
    try {
      const url = new URL(value,win.location.origin);
      if (url.origin !== win.location.origin || !['http:','https:'].includes(url.protocol)) return null;
      const prefix = config?.suggest?.pathname.replace(/search\/suggest$/,'') || '/';
      if (prefix !== '/' && /^\/(products|pages|blogs|collections)\//.test(url.pathname)) url.pathname = prefix.replace(/\/$/,'') + url.pathname;
      if (removeVariant) url.searchParams.delete('variant');
      return url.href;
    } catch (_) { return null; }
  }
  function nodes(form) {
    const list = form.querySelector('.search__results'), wrapper = form.querySelector('.search__results-wrapper');
    return list && wrapper ? {list,wrapper} : null;
  }
  function allResults(config,form,term,type) {
    const li = win.document.createElement('li');li.className = 'all-results';
    const a = win.document.createElement('a');a.href = searchURL(config,form,term,type);a.className = 'search-title see-all';
    a.textContent = win.PXUTheme?.translation?.all_results || 'View all results';li.append(a);return li;
  }
  function show(form, items, found) {
    const target = nodes(form);if (!target) return false;
    // Turbo hides the wrapper in its stylesheet, so removing an inline value
    // hides successfully rendered results again. Both nodes need an override.
    target.list.replaceChildren(...items);target.list.style.display = 'block';
    target.wrapper.style.display = 'block';target.wrapper.classList.toggle('results-found',found);return true;
  }
  function hide(form) {
    const target = nodes(form);if (!target) return;
    target.list.replaceChildren();target.list.style.display = 'none';
    target.wrapper.style.display = 'none';target.wrapper.classList.remove('results-found');
  }
  function cancel(state) {
    // Invalidate before aborting: an abort listener may run synchronously.
    state.generation++;
    if (state.timer !== null) win.clearTimeout(state.timer);
    const controller = state.controller, settle = state.settle;
    state.timer = state.controller = state.pending = state.settle = state.key = null;
    try {controller?.abort();} catch (_) {/* Some browser abort implementations throw. */}
    if (settle) settle('stale');
  }
  function clear(form) {
    if (!win || !form || typeof form.querySelector !== 'function') return;
    const state = states.get(form);if (state) cancel(state);
    hide(form);
  }
  function linkOnly(config,form,term,type) { return show(form,[allResults(config,form,term,type)],false); }
  function sectionItems(html, config, form, term, type) {
    if (typeof html !== 'string' || html.length > 500000) return null;
    const document = new win.DOMParser().parseFromString(html,'text/html');
    const roots = document.querySelectorAll('[data-optional-case-search-results]');
    if (roots.length !== 1) return null;
    const root = roots[0];
    if (root.getAttribute('data-version') !== '1' || root.getAttribute('data-query') !== term || root.querySelector('script,style,iframe,object,embed,form') || [...root.children].some(child => child.tagName !== 'LI')) return null;
    for (const element of root.querySelectorAll('*')) {
      if ([...element.attributes].some(attribute => /^on/i.test(attribute.name))) return null;
      if (element.hasAttribute('href')) {
        const href = resourceURL(element.getAttribute('href'),config,false);if (!href) return null;element.setAttribute('href',href);
      }
      if (element.hasAttribute('src')) {
        try { const src = new URL(element.getAttribute('src'),win.location.origin);if (!['http:','https:'].includes(src.protocol)) return null; }
        catch (_) { return null; }
      }
    }
    const items = [...root.children].map(child => win.document.importNode(child,true));
    const all = items.find(item => item.classList.contains('all-results'));
    if (!all) return null;
    const link = all.querySelector('a');if (!link) return null;
    link.href = searchURL(config,form,term,type);link.textContent = win.PXUTheme?.translation?.all_results || 'View all results';
    return {items,found:items.some(item => item.hasAttribute('data-optional-case-search-item'))};
  }
  function amount(value) {
    if (!['string','number'].includes(typeof value) || !/^\d+(?:\.\d+)?$/.test(String(value))) return null;
    const parsed = Number(value);return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }
  function price(value,config) {
    try { return new Intl.NumberFormat(win.document.documentElement.lang || 'en',{style:'currency',currency:config.currency,currencyDisplay:'code'}).format(value); }
    catch (_) { return value.toFixed(2) + ' ' + config.currency; }
  }
  function jsonItems(response,config,form,term,type) {
    const resources = response?.resources?.results;
    if (!resources || typeof resources !== 'object' || !['products','pages','articles'].some(key => Array.isArray(resources[key]))) return null;
    const items = [allResults(config,form,term,type)];
    for (const [key,kind] of [['products','product'],['pages','page'],['articles','article']]) {
      if (resources[key] !== undefined && !Array.isArray(resources[key])) return null;
      for (const result of (resources[key] || [])) {
        if (!result || typeof result.title !== 'string' || typeof result.url !== 'string') continue;
        const href = resourceURL(result.url,config,kind === 'product');if (!href) continue;
        const li = win.document.createElement('li');li.className = 'item-result';li.setAttribute('data-optional-case-search-item','');
        const link = win.document.createElement('a');link.href = href;link.tabIndex = 0;
        const title = win.document.createElement('div');title.className = 'search-title';title.textContent = result.title;link.append(title);
        if (kind === 'product') {
          const money = win.document.createElement('span');money.className = 'item-pricing price';
          const minimum = amount(result.price_min ?? result.price);
          // JSON omits our metafields and exact variant pair. A native entry/From
          // amount is useful; an aggregate max compare-at would be misleading.
          money.textContent = result.available !== true ? (win.PXUTheme?.theme_settings?.sold_out_text || 'Sold out') : minimum === null ? 'See product for price' : (win.PXUTheme?.translation?.from_text || 'From') + ' ' + price(minimum,config);
          title.append(win.document.createElement('br'),money);
        }
        li.append(link);items.push(li);
        if (items.length >= 31) break;
      }
      if (items.length >= 31) break;
    }
    if (items.length === 1) {
      const empty = win.document.createElement('li');empty.className = 'item-result';
      empty.textContent = win.PXUTheme?.translation?.no_results || 'No results';items.push(empty);
    }
    return {items,found:items.some(item => item.hasAttribute('data-optional-case-search-item'))};
  }
  function aborted(error,signal) {return signal?.aborted === true || error?.name === 'AbortError';}
  async function retrieve(url,config,form,term,type,active,controller) {
    const init = {credentials:'same-origin',signal:controller?.signal};
    const cooldownKey = config.suggest.href + '|' + config.currency;
    if ((sectionCooldowns.get(cooldownKey) || 0) <= win.Date.now()) {
      sectionCooldowns.delete(cooldownKey);
      try {
        const response = await win.fetch(url.href,{...init,headers:{Accept:'text/html'}});
        if (!active()) return 'stale';
        if (!response.ok) {
          // A short, page-local circuit breaker avoids hitting a struggling
          // section for every keystroke. No prices are cached or retried.
          if ([429,503].includes(response.status)) sectionCooldowns.set(cooldownKey,win.Date.now() + cooldownMilliseconds);
          throw new Error('SECTION_UNAVAILABLE');
        }
        const html = await response.text();if (!active()) return 'stale';
        const rendered = sectionItems(html,config,form,term,type);if (!rendered) throw new Error('SECTION_INVALID');
        show(form,rendered.items,rendered.found);return 'section';
      } catch (error) {
        if (!active()) return 'stale';
        if (aborted(error,init.signal)) return 'aborted';
      }
    }
    if (!active()) return 'stale';
    const jsonURL = new URL(url.href);jsonURL.pathname += '.json';jsonURL.searchParams.delete('section_id');
    try {
      const response = await win.fetch(jsonURL.href,{...init,headers:{Accept:'application/json'}});
      if (!active()) return 'stale';if (!response.ok) throw new Error('JSON_UNAVAILABLE');
      const data = await response.json();if (!active()) return 'stale';
      const rendered = jsonItems(data,config,form,term,type);if (!rendered) throw new Error('JSON_INVALID');
      show(form,rendered.items,rendered.found);return 'json';
    } catch (error) {
      if (!active()) return 'stale';
      if (aborted(error,init.signal)) return 'aborted';
      linkOnly(config,form,term,type);return 'link';
    }
  }
  function request(term,form,options) {
    if (!win || !form || typeof form.querySelector !== 'function' || !nodes(form)) return Promise.resolve('unavailable');
    term = String(term || '').trim();options = options || {};
    if (term.length <= 3) {clear(form);return Promise.resolve('ignored');}
    const config = configuration(), type = types(options.type);
    const limit = Math.min(10,Math.max(1,Number.isSafeInteger(Number(options.limit)) ? Number(options.limit) : 4));
    let state = states.get(form);
    if (!state) {state = {generation:0,controller:null,timer:null,pending:null,settle:null,key:null};states.set(form,state);}
    const key = JSON.stringify([term,type,limit,config?.suggest.href,config?.search.href,config?.currency]);
    if (state.pending && state.key === key) return state.pending;
    cancel(state);hide(form);
    const generation = state.generation;
    const active = () => form.isConnected && state.generation === generation && String(form.querySelector('input[name="q"]')?.value || '').trim() === term;
    if (!config || typeof win.fetch !== 'function' || typeof win.DOMParser !== 'function') {
      if (!active()) return Promise.resolve('stale');
      linkOnly(config,form,term,type);return Promise.resolve('link');
    }
    const url = new URL(config.suggest.href);url.searchParams.set('q',term);url.searchParams.set('section_id',config.sectionId);
    url.searchParams.set('resources[type]',type);url.searchParams.set('resources[limit]',String(limit));
    url.searchParams.set('resources[options][unavailable_products]','last');
    url.searchParams.set('resources[options][fields]','title,body,variants.title,vendor,product_type,tag');
    state.key = key;
    const pending = new Promise(resolve => {
      let settled = false;
      const finish = value => {
        if (settled) return;settled = true;
        if (state.generation === generation) state.timer = state.controller = state.pending = state.settle = state.key = null;
        resolve(value);
      };
      state.settle = finish;
      state.timer = win.setTimeout(() => {
        state.timer = null;
        if (!active()) {finish('stale');return;}
        let controller = null;
        try {if (typeof win.AbortController === 'function') controller = new win.AbortController();} catch (_) {/* Request generations still prevent stale results. */}
        state.controller = controller;
        retrieve(url,config,form,term,type,active,controller).then(finish,error => {
          // Public requests are deliberately resolve-only: the legacy keyup
          // integration does not await them, including intentional aborts.
          if (!active()) {finish('stale');return;}
          if (aborted(error,controller?.signal)) {finish('aborted');return;}
          linkOnly(config,form,term,type);finish('link');
        });
      },debounceMilliseconds);
    });
    state.pending = pending;return pending;
  }
  return {request,clear};
});
